import { UTCDate, UTCDateMini } from '@date-fns/utc';
import { Icons, Stack, Tooltip, Typography } from '@healthinal/ui';
import { useNavigate } from '@tanstack/react-router';
import { differenceInMilliseconds } from 'date-fns';
import { RefObject } from 'react';
import {
  DirectlyObservedTherapyDto,
  PatientSummaryDto,
  TherapyComplianceDto,
  TherapyDto,
} from '../../../../api/generated/model';
import { TooltipCell } from '../../../../components/table/TooltipCell.tsx';
import { VirtualTablePaddingRow } from '../../../../components/table/VirtualTablePaddingRow.tsx';
import { useCustomVirtualizer } from '../../../../hooks/useCustomVirtualizer.ts';
import { PATIENT_TABLE_CELL_HEIGHT } from '../../../../theme.ts';
import { getLatestTherapyCondition } from '../../../../utils/condition.ts';
import { formatDate, getCurrentDate, getDifferenceInDays, getDifferenceInMonths } from '../../../../utils/date.ts';
import { getDirectlyObservedTherapy } from '../../../../utils/therapy.ts';
import { PatientTable } from './PatientTable.tsx';
import { PatientTablePatientInfosData, PatientTablePatientInfosHeader } from './PatientTablePatientInfos.tsx';

export function PatientsTherapyTable({
  patients,
  parentRef,
}: {
  patients: PatientSummaryDto[];
  parentRef: RefObject<HTMLElement | null>;
}) {
  const navigate = useNavigate();

  const { virtualItems, totalSize, paddingTop, paddingBottom } = useCustomVirtualizer({
    count: patients.length,
    cellHeight: PATIENT_TABLE_CELL_HEIGHT,
    parentRef: parentRef,
  });

  return (
    <PatientTable stickyHeader borderAxis="xBetween" noWrap hoverRow>
      <thead>
        <tr>
          <th style={{ width: 130, minWidth: 130 }}>Fallnummer</th>
          <PatientTablePatientInfosHeader showCreatedAt={false} />
          <th style={{ width: 130, minWidth: 130 }}>Therapiestart</th>
          <th style={{ width: 200, minWidth: 200 }}>Geplante Therapiedauer</th>
          <th style={{ width: 80, minWidth: 80 }}>Phase</th>
          <th style={{ width: 120, minWidth: 120 }}>Hospitalisiert</th>
          <th style={{ width: 125, minWidth: 125 }}>DOT</th>
          <th style={{ width: 180, minWidth: 180 }}>Apotheke</th>
          <th style={{ width: 125, minWidth: 125 }}>Compliance</th>
          <th style={{ width: 135, minWidth: 135 }}>Befinden</th>
        </tr>
      </thead>
      <tbody style={{ height: totalSize }}>
        <VirtualTablePaddingRow height={paddingTop} />
        {virtualItems.map((virtualRow) => {
          const patient = patients[virtualRow.index];
          const directlyObservedTherapy = getDirectlyObservedTherapy(patient.therapy?.directlyObservedTherapy);
          return (
            <tr
              key={virtualRow.index}
              onClick={() => navigate({ to: '/patients/$patientId/therapy', params: { patientId: patient.id } })}>
              <TooltipCell>{patient.caseNumber}</TooltipCell>
              <PatientTablePatientInfosData patient={patient} showCreatedAt={false} />
              <td>{formatDate(patient.therapy?.startPhase1)}</td>
              <td>{getPlannedTherapyDuration(patient.therapy)}</td>
              <td>{getCurrentPhase(patient.therapy)}</td>
              <td>{patient.isHospitalized ? 'Ja' : 'Nein'}</td>
              <td>
                {directlyObservedTherapy && getDirectlyObservedTherapyStartDate(directlyObservedTherapy)}
              </td>
              <TooltipCell>{directlyObservedTherapy?.executor?.address?.organization}</TooltipCell>
              <td>
                {patient.therapy?.therapyCompliance &&
                  formatDate(getLatestComplianceDate(patient.therapy.therapyCompliance))}
              </td>
              <td>{patient.therapy?.conditionIntervalDays && getLatestConditionDate(patient.therapy)}</td>
            </tr>
          );
        })}
        <VirtualTablePaddingRow height={paddingBottom} />
      </tbody>
    </PatientTable>
  );
}

function getPlannedTherapyDuration(therapy: TherapyDto | undefined | null) {
  if (!therapy?.startPhase1 || !therapy.end) return undefined;
  return `${getDifferenceInMonths(therapy.startPhase1, therapy.end)} Monat(e)`;
}

function getDirectlyObservedTherapyStartDate(directlyObservedTherapy: DirectlyObservedTherapyDto) {
  const applicantFrom = directlyObservedTherapy.applicant?.from;
  const executorFrom = directlyObservedTherapy.executor?.from;

  if (!applicantFrom && !executorFrom) {
    return undefined;
  }

  if (!applicantFrom) {
    return formatDate(executorFrom);
  }

  if (!executorFrom) {
    return formatDate(applicantFrom);
  }

  const applicantFromDate = new UTCDateMini(applicantFrom).getTime();
  const executorFromDate = new UTCDate(executorFrom).getTime();

  return applicantFromDate > executorFromDate ? formatDate(applicantFrom) : formatDate(executorFrom);
}

function getLatestComplianceDate(compliance: TherapyComplianceDto[]) {
  if (!compliance.length) return undefined;
  return compliance.reduce(
    (latest, current) => (differenceInMilliseconds(current.date, latest.date) > 0 ? current : latest),
    compliance[0],
  ).date;
}

function getLatestConditionDate(therapy: TherapyDto) {
  const latestConditionDate = getLatestTherapyCondition(therapy.therapyCondition)?.date;

  if (!therapy.conditionIntervalDays || !latestConditionDate) return undefined;

  const differenceInDays = getDifferenceInDays(latestConditionDate, getCurrentDate());
  if (differenceInDays >= therapy.conditionIntervalDays && !therapy.finished) {
    return (
      <Tooltip title={`Rückfrage seit ${differenceInDays - therapy.conditionIntervalDays} Tagen überfällig`}>
        <Typography color={'warning'} endDecorator={<Icons.ErrorOutline color={'warning'} size={'sm'} />}>
          {formatDate(latestConditionDate)}
        </Typography>
      </Tooltip>
    );
  }
  return formatDate(latestConditionDate);
}

function getCurrentPhase(therapy: TherapyDto | undefined | null) {
  if (therapy?.finished)
    return (
      <Tooltip title={'Die Therapie wurde als abgeschlossen markiert'}>
        <Stack alignContent={'center'}>
          <Icons.CheckCircleOutlined color={'success'} size={'sm'} />
        </Stack>
      </Tooltip>
    );

  if (!therapy?.startPhase1 || !therapy.end)
    return (
      <Tooltip title={'Noch keine Therapie vorhanden'}>
        <Stack alignContent={'center'}>
          <Icons.PendingOutlined size={'sm'} />
        </Stack>
      </Tooltip>
    );

  const startPhase1Date = new UTCDateMini(therapy.startPhase1).getTime();
  const startPhase2Date = therapy.startPhase2 ? new UTCDateMini(therapy.startPhase2).getTime() : 0;
  const endDate = new UTCDateMini(therapy.end).getTime();
  const today = UTCDateMini.now();

  if (today > endDate && !therapy.finished)
    return (
      <Tooltip
        title={
          'Das Enddatum der Therapie wurde überschritten. Sie können das Enddatum anpassen oder die Therapie beenden.'
        }>
        <Stack alignContent={'center'}>
          <Icons.HelpOutline color={'warning'} size={'sm'} />
        </Stack>
      </Tooltip>
    );
  if (today < startPhase1Date) return undefined;
  if (today >= startPhase1Date && (today < startPhase2Date || startPhase2Date === 0)) return 1;
  if (today >= startPhase2Date && startPhase2Date !== null) return 2;
}
